import KPICards from "@/components/recap/KPICards";
import Header from "@/components/recap/sessionheader/Header";
import SessionInformation from "@/components/recap/sessionInformation/SessionInformation";
import Summary from "@/components/recap/Summary";
import RecapResponsiveSkeleton from "@/components/skeleton/RecapResponsiveSkeleton";
import {
  useMeetDataByKey,
  useRecapActionItemsBySessionId,
  useRecapDecisionsBySessionId,
  useRecapOverviewBySessionId,
  useRecapParticipantsBySessionId,
  useRecapSessionsByMeetKey,
  useSessionDataBySessionId,
} from "@/features/hooks/useRecap";
import useResponsive from "@/hooks/useResponsive.hook";
import { formatPersianMeetingLabel } from "@/middleware/Date";
import { Flex, Typography } from "antd";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router";

const { Text } = Typography;

const RecapContent = ({ meetingId }: { meetingId: string }) => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("sid");
  const navigate = useNavigate();

  const isResponsive = useResponsive();

  const [selectedSession, setSelectedSession] = useState<string | null>(null);

  const { data: meetData } = useMeetDataByKey(meetingId);

  const {
    data: recapSessions,
    isPending: isSessionsLoading,
    refetch: fetchRecapSessionsByMeetKey,
  } = useRecapSessionsByMeetKey(meetingId || "");

  const { data: participants, isPending: isLoadingParticipants } =
    useRecapParticipantsBySessionId(selectedSession || "");

  const { data: recapOverview, isPending: isOverviewLoading } =
    useRecapOverviewBySessionId(selectedSession || "");

  const { data: sessionData } = useSessionDataBySessionId(
    selectedSession || ""
  );

  const onSessionChange = (value: string) => {
    setSelectedSession(value);

    const params = new URLSearchParams(searchParams);
    params.set("sid", value);

    navigate(
      {
        pathname: `/recap/${meetingId}`,
        search: `?${params.toString()}`,
      },
      { replace: true }
    );
  };

  const options = useMemo(() => {
    const totalEvents = recapSessions?.length || 0;

    return (recapSessions ?? []).map((item, index) => {
      const eventLabel = t("recap.eventOfTotal", {
        current: index + 1,
        total: totalEvents,
      });

      const timeLabel = formatPersianMeetingLabel(item);

      return {
        value: item?.id,
        plainLabel: timeLabel,
        label: <Text ellipsis>{timeLabel}</Text>,

        selectedLabel: (
          <Flex
            align={isResponsive ? "" : "center"}
            vertical={isResponsive}
            gap={isResponsive ? 0 : 8}
          >
            <Text type="secondary" className="h7">
              {eventLabel}
            </Text>
            <Text ellipsis>{timeLabel}</Text>
          </Flex>
        ),
      };
    });
  }, [recapSessions, t, isResponsive]);

  const selectedSessionOption = useMemo(
    () => options?.find((item: any) => item?.value === selectedSession),
    [options, selectedSession]
  );

  const activeSession = useMemo(
    () => recapSessions?.find((item) => item.id === selectedSession),
    [recapSessions, selectedSession]
  );

  const { data: actionItems } = useRecapActionItemsBySessionId(
    selectedSession || ""
  );
  const { data: decisions } = useRecapDecisionsBySessionId(
    selectedSession || ""
  );

  const printData = useMemo(
    () => ({
      meetTitle: meetData?.title || "",
      selectedSessionLabel: selectedSessionOption?.plainLabel,
      participants,
      recapOverview,
      summary: sessionData?.summary?.trim(),
      actionItems,
      decisions,
    }),
    [
      meetData?.title,
      selectedSessionOption?.plainLabel,
      participants,
      recapOverview,
      sessionData?.summary,
      actionItems,
      decisions,
    ]
  );

  useEffect(() => {
    if (!recapSessions?.length) return;

    const hasValidSelectedSession = recapSessions.some(
      (session) => session?.id === selectedSession
    );

    const hasValidSidInQuery = sessionId
      ? recapSessions.some((session) => session?.id === sessionId)
      : false;

    if (hasValidSidInQuery && selectedSession !== sessionId) {
      setSelectedSession(sessionId);
      return;
    }

    if (!selectedSession || !hasValidSelectedSession) {
      setSelectedSession(recapSessions[0]?.id);
    }
  }, [recapSessions, selectedSession, sessionId]);

  useEffect(() => {
    if (searchParams?.get("token")) {
      localStorage.setItem("recap_token", searchParams.get("token") || "");
    } else {
      localStorage.removeItem("recap_token");
    }
  }, [searchParams]);

  useEffect(() => {
    if (meetingId && !recapSessions?.length) {
      fetchRecapSessionsByMeetKey();
    }
  }, [meetingId, recapSessions?.length, fetchRecapSessionsByMeetKey]);

  return (
    <Flex gap={16} className="recap-layout-wrapper">
      {isSessionsLoading && isResponsive ? (
        <RecapResponsiveSkeleton />
      ) : (
        <Flex vertical gap={16}>
          <Header
            meetTitle={meetData?.title || ""}
            selectedSession={selectedSession || ""}
            onSessionChange={onSessionChange}
            placeholder={t("actions.placeholder.selectMeet")}
            options={options}
            eventType={meetData?.type}
            overview={sessionData?.description?.trim()}
            participants={participants}
            isLoadingParticipants={isLoadingParticipants}
            printData={printData}
          />

          <KPICards overview={recapOverview} isLoading={isOverviewLoading} />

          <Summary summary={sessionData?.summary?.trim()} />

          <SessionInformation
            selectedSessionId={selectedSession || ""}
            currentSession={activeSession || ""}
            participants={participants}
            isLoadingParticipants={isLoadingParticipants}
            minutes={sessionData?.minutes || ""}
          />
        </Flex>
      )}
    </Flex>
  );
};

export default RecapContent;
